
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { WaitlistFormValues } from "@/components/waitlist/schema";
import { useTransactionalEmail } from "@/hooks/use-transactional-email";
import { processWaitlistSubmission, processNursingHomeLeads } from "@/utils/waitlist-processor";

interface UseWaitlistSubmissionProps {
  nursingHomeName?: string;
  countyName?: string;
  nursingHomeSlug?: string;
}

export function useWaitlistSubmission({ 
  nursingHomeName, 
  countyName, 
  nursingHomeSlug 
}: UseWaitlistSubmissionProps) {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { sendEmail } = useTransactionalEmail();

  async function submitWaitlistForm(data: WaitlistFormValues, userId: string) {
    try {
      setIsSubmitting(true);
      console.log("Starting waitlist form submission process...");
      
      // Process waitlist submission
      await processWaitlistSubmission(userId, data);
      console.log("Waitlist submission successfully processed");

      // Process nursing home leads
      const nursingHomeLeads = await processNursingHomeLeads(userId, data, nursingHomeName, nursingHomeSlug, countyName);
      console.log("Nursing home leads successfully processed:", nursingHomeLeads);

      // Send confirmation emails
      console.log("Preparing to send confirmation emails...");
      const dashboardUrl = `${window.location.origin}/dashboard`;
      
      // Collect nursing home names for the email
      const nursingHomes: string[] = [];
      
      // Add explicitly requested nursing home if provided
      if (nursingHomeName) {
        nursingHomes.push(nursingHomeName);
      }
      
      // Parse nursing homes from form data if it's a comma-separated string
      if (data.specificNursingHome && data.specificNursingHome !== 'open_to_suggestions') {
        // Get nursing home names from database based on the slugs
        const slugs = data.specificNursingHome.split(',').filter(Boolean);
        
        if (slugs.length > 0) {
          const { data: nhData } = await supabase
            .from('nursing_homes')
            .select('center_name, nh_slug, county_slug')
            .in('nh_slug', slugs);
            
          if (nhData) {
            // For each nursing home, send an anonymized lead notification
            for (const nh of nhData) {
              if (nh.center_name) {
                nursingHomes.push(nh.center_name);
                
                // Create a lead record in the database
                const { data: leadData } = await supabase.from('nursing_home_leads').insert({
                  user_id: userId,
                  nursing_home_id: nh.nh_slug,
                  nursing_home_name: nh.center_name,
                  location: nh.county_slug,
                  status: 'waitlist',
                  notes: 'Added from waitlist form'
                }).select();
                
                const leadId = leadData && leadData[0] ? leadData[0].id : undefined;
                
                // Send anonymized lead notification to each nursing home
                try {
                  const claimLink = `${window.location.origin}/nursing-homes/${nh.county_slug}/${nh.nh_slug}?action=claim`;
                  
                  await sendEmail({
                    type: 'nursing_home_inquiry',
                    data: {
                      nursingHomeName: nh.center_name,
                      county: nh.county_slug,
                      message: "A qualified lead has joined your waitlist through Grandpal. They're actively looking to secure a place and would like to connect. Claim your profile to access this lead's contact details.",
                      email: "hello@updates.grandpal.co", // Default to Grandpal for waitlist form
                      claimLink,
                      leadId
                    }
                  });
                } catch (emailError) {
                  console.error(`Failed to send lead notification for ${nh.center_name}:`, emailError);
                }
              }
            }
          }
        }
      }
      
      // Send waitlist confirmation email to user
      try {
        await sendEmail({
          type: 'waitlist_confirmation',
          data: {
            firstName: data.firstName,
            email: data.email,
            dashboardUrl,
            nursingHomes: nursingHomes.length > 0 ? nursingHomes : undefined,
          }
        });
        
        // If there are nursing homes selected, also send the nursing home leads email
        if (nursingHomes.length > 0) {
          try {
            await sendEmail({
              type: 'nursing_home_leads',
              data: {
                firstName: data.firstName,
                email: data.email,
                nursingHomes,
                dashboardUrl
              }
            });
            console.log("Nursing home leads email sent successfully");
          } catch (leadsEmailError) {
            console.error("Error sending nursing home leads email:", leadsEmailError);
          }
        }
        
        toast.success("Confirmation email sent", {
          description: "Check your inbox for submission details."
        });
      } catch (emailError: any) {
        console.error("Error sending confirmation email:", emailError);
        toast.error("Could not send confirmation email", {
          description: "Your submission was saved but we couldn't send the confirmation email."
        });
      }
      
      toast.success("Waitlist form submitted", {
        description: "Thank you for your interest. We'll be in touch soon."
      });
      
      navigate("/dashboard");
      return true;
    } catch (error: any) {
      console.error("Error submitting waitlist form:", error);
      toast.error("Failed to submit waitlist form", { 
        description: error.message || "Please try again."
      });
      return false;
    } finally {
      setIsSubmitting(false);
    }
  }

  function handleUnauthenticatedSubmission(data: WaitlistFormValues) {
    sessionStorage.setItem('waitlistFormData', JSON.stringify(data));
    
    const params = new URLSearchParams();
    params.append('from', 'waitlist');
    params.append('auth', 'needed');
    
    if (nursingHomeName) params.append('nursing_home', nursingHomeName);
    if (countyName) params.append('county', countyName);
    if (nursingHomeSlug) params.append('nh_slug', nursingHomeSlug);
    
    toast.info("Please create an account to save your waitlist registration", {
      description: "Your form data will be preserved."
    });
    
    navigate(`/thank-you?${params.toString()}`);
  }

  return {
    isSubmitting,
    submitWaitlistForm,
    handleUnauthenticatedSubmission
  };
}
